import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { Sobreposicao } from '@/componentes/JanelaDeAcao';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { Seletor } from '@/componentes/Seletor';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { formatarDataHora, formatarInteiro } from '@/lib/formato';
import { comCorpo, explicarFalha, pedir } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { useFrota } from '@/modulos/equipamentos/consultas-frota';

import { taxaOuNada, type Placar } from './qualidadeDaIa';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcoesComVisual';

interface Caso {
  readonly id: string;
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly pergunta: string;
  readonly documento: string | null;
  readonly pagina: number | null;
  readonly trechoEsperado: string | null;
  readonly respostaAceitavel: string | null;
  readonly deveAbster: boolean;
  readonly condicaoDeAbstencao: string | null;
  readonly termosProibidos: string | null;
  readonly ativo: boolean;
}

interface Rodada {
  readonly id: string;
  readonly situacao: 'rodando' | 'concluida' | 'falhou';
  readonly pedidaEm: string;
  readonly concluidaEm: string | null;
  readonly casos: number;
  readonly erro: string | null;
  readonly pedidaPor: string | null;
  readonly parada: boolean;
  readonly placar: Placar;
}

interface ResultadoDoCaso {
  readonly casoId: string;
  readonly pergunta: string;
  readonly equipamento: string;
  readonly deveAbster: boolean;
  readonly sabe: boolean;
  readonly resposta: string;
  readonly recuperou: boolean | null;
  readonly citouOEsperado: boolean | null;
  readonly correto: boolean;
  readonly violouProibicao: boolean;
}

const SITUACAO: Record<Rodada['situacao'], string> = { rodando: 'Rodando', concluida: 'Concluída', falhou: 'Não terminou' };

/**
 * O conjunto de avaliação (guia 15.1): os casos escritos por quem conhece a
 * máquina, o botão que roda todos pelo caminho da pergunta, e as rodadas com o
 * placar. Cada rodada tem custo — um pedido ao serviço por caso —, e o botão
 * diz quantos casos vão rodar.
 */
export function ConjuntoDeAvaliacao({
  rodadaAberta,
  aoAbrirRodada,
}: {
  /* O painel de detalhe mora FORA da área que rola (p.28): quem o abre é a página. */
  readonly rodadaAberta: string | null;
  readonly aoAbrirRodada: (id: string) => void;
}) {
  const cliente = useQueryClient();
  const [criando, setCriando] = useState(false);

  const casos = useQuery({
    queryKey: ['ia', 'avaliacao', 'casos'],
    queryFn: () => pedir<{ casos: readonly Caso[] }>('/ia/avaliacao/casos'),
    meta: { oQueLia: 'os casos de avaliação' },
  });
  const rodadas = useQuery({
    queryKey: ['ia', 'avaliacao', 'rodadas'],
    queryFn: () => pedir<{ rodadas: readonly Rodada[]; motorConfigurado: boolean }>('/ia/avaliacao/rodadas'),
    /* Enquanto uma rodada roda, a lista se atualiza sozinha: a pessoa vê os casos passando. */
    refetchInterval: (q) => (q.state.data?.rodadas.some((r) => r.situacao === 'rodando' && !r.parada) ? 3000 : false),
    meta: { oQueLia: 'as rodadas de avaliação' },
  });
  const rodar = useMutation({
    mutationFn: () => comCorpo<{ id: string; casos: number }>('POST', '/ia/avaliacao/rodadas', {}),
    meta: {
      retorno: retornoDe<{ casos: number }>((r) => ({
        titulo: 'Rodada de avaliação iniciada',
        alvo: { nome: 'Conjunto de avaliação da IA' },
        fatos: [`${formatarInteiro(r.casos)} ${r.casos === 1 ? 'caso' : 'casos'}`],
        efeito: 'Os casos passam um a um; a lista mostra o andamento',
      })),
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['ia', 'avaliacao', 'rodadas'] });
    },
  });
  const alternar = useMutation({
    mutationFn: (c: Caso) => comCorpo<Caso>('PATCH', `/ia/avaliacao/casos/${c.id}`, { ativo: !c.ativo }),
    meta: { silencioso: true },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['ia', 'avaliacao', 'casos'] });
    },
  });

  const lista = casos.data?.casos ?? [];
  const paginaDosCasos = usePaginacao(lista);
  const paginaDasRodadas = usePaginacao(rodadas.data?.rodadas ?? []);
  const ativos = lista.filter((c) => c.ativo).length;
  const emCurso = (rodadas.data?.rodadas ?? []).some((r) => r.situacao === 'rodando' && !r.parada);

  const colunasDosCasos: readonly Coluna<Caso>[] = [
    {
      chave: 'pergunta',
      rotulo: 'Pergunta',
      prioridade: 'chave',
      celula: (c) => (
        <span className="flex min-w-0 flex-col">
          <TextoDaCelula texto={c.pergunta} />
          <span className="txt-apoio text-tinta-apagada">{c.equipamento}</span>
        </span>
      ),
    },
    {
      chave: 'certo',
      rotulo: 'O que conta como certo',
      celula: (c) => (
        <TextoDaCelula
          texto={
            c.deveAbster
              ? `Abster-se: ${c.condicaoDeAbstencao ?? ''}`
              : [
                  c.documento ? `${c.documento}${c.pagina ? `, p. ${String(c.pagina)}` : ''}` : null,
                  c.trechoEsperado ? `trecho “${c.trechoEsperado}”` : null,
                  c.respostaAceitavel ? `termos: ${c.respostaAceitavel}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
          }
          classe="txt-apoio text-tinta-secundaria"
        />
      ),
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      alinhamento: 'status',
      celula: (c) => (c.ativo ? 'Entra na rodada' : 'Fora da rodada'),
    },
  ];

  const colunasDasRodadas: readonly Coluna<Rodada>[] = [
    {
      chave: 'quando',
      rotulo: 'Quando',
      prioridade: 'chave',
      celula: (r) => (
        <span className="flex flex-col">
          <span className="txt-corpo whitespace-nowrap text-tinta tabular-nums">{formatarDataHora(new Date(r.pedidaEm))}</span>
          <span className="txt-apoio text-tinta-apagada">
            {r.situacao === 'rodando' && r.parada ? 'Parou sem terminar' : SITUACAO[r.situacao]} · {formatarInteiro(r.casos)} {r.casos === 1 ? 'caso' : 'casos'}
          </span>
        </span>
      ),
    },
    { chave: 'precisao', rotulo: 'Precisão', alinhamento: 'numero', celula: (r) => taxaOuNada(r.placar.precisao) },
    { chave: 'abstencao', rotulo: 'Abstenção', alinhamento: 'numero', celula: (r) => taxaOuNada(r.placar.abstencao) },
    { chave: 'recuperacao', rotulo: 'Recuperação', alinhamento: 'numero', prioridade: 'secundaria', celula: (r) => taxaOuNada(r.placar.recuperacao) },
    { chave: 'violacoes', rotulo: 'Disse o proibido', alinhamento: 'numero', celula: (r) => formatarInteiro(r.placar.violacoes) },
  ];

  const erroDe = (q: { readonly error: unknown; readonly refetch: () => unknown }, oQue: string) => (
    <EstadoVazio
      tom="erro"
      titulo={`Não foi possível carregar ${oQue}`}
      descricao={`${explicarFalha(q.error).erro}. ${explicarFalha(q.error).comoResolver}`}
      acao={<Botao nivel="secundario" onClick={() => { void q.refetch(); }}>Carregar de novo</Botao>}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="txt-apoio max-w-[70ch] text-tinta-secundaria">
          Perguntas reais da obra, com a resposta certa escrita antes. Cada rodada passa os casos ativos pelo mesmo caminho da pergunta à
          máquina e mede precisão, abstenção e recuperação (guia, seções 6.3 e 15.1). Cada caso é um pedido pago ao serviço de IA.
        </p>
        <div className="flex gap-2">
          <Botao nivel="secundario" onClick={() => { setCriando(true); }}>
            Novo caso
          </Botao>
          <Botao
            nivel="primario"
            disabled={ativos === 0 || emCurso || rodar.isPending || rodadas.data?.motorConfigurado === false}
            onClick={() => {
              rodar.mutate();
            }}
          >
            {emCurso ? 'Rodada em andamento' : `Rodar ${formatarInteiro(ativos)} ${ativos === 1 ? 'caso' : 'casos'}`}
          </Botao>
        </div>
      </div>

      <Tabela
        rotulo="Casos de avaliação"
        colunas={colunasDosCasos}
        linhas={paginaDosCasos.visiveis}
        rodape={<RodapeTabela {...paginaDosCasos.rodape} />}
        alturaDupla
        chaveDaLinha={(c) => c.id}
        carregando={casos.isPending}
        erro={casos.isError ? erroDe(casos, 'os casos') : null}
        acoes={(c) => (
          <Botao tamanho="p" nivel="fantasma" disabled={alternar.isPending} onClick={() => { alternar.mutate(c); }}>
            {c.ativo ? 'Tirar da rodada' : 'Pôr na rodada'}
          </Botao>
        )}
        vazio={
          <EstadoVazio
            icone={ClipboardCheck}
            titulo="O conjunto de avaliação está vazio"
            descricao="Comece pelas perguntas que mecânicos, planejadores e almoxarifado fazem de verdade, com a página do manual que responde. Inclua uma pergunta que o manual não responde: a IA tem de dizer que não sabe."
            acao={<Botao nivel="secundario" onClick={() => { setCriando(true); }}>Novo caso</Botao>}
          />
        }
      />

      <h3 className="txt-secao text-tinta">Rodadas</h3>
      <div data-painel-origem>
        <Tabela
          rotulo="Rodadas de avaliação"
          colunas={colunasDasRodadas}
          linhas={paginaDasRodadas.visiveis}
          rodape={<RodapeTabela {...paginaDasRodadas.rodape} />}
          alturaDupla
          chaveDaLinha={(r) => r.id}
          carregando={rodadas.isPending}
          selecionada={rodadaAberta}
          aoSelecionar={(r) => {
            aoAbrirRodada(r.id);
          }}
          erro={rodadas.isError ? erroDe(rodadas, 'as rodadas') : null}
          vazio={
            <EstadoVazio
              titulo="Nenhuma rodada ainda"
              descricao={
                rodadas.data?.motorConfigurado === false
                  ? 'O motor de IA não está ligado neste ambiente. Peça a quem administra a plataforma para configurá-lo.'
                  : 'Cadastre os casos e rode a avaliação: o placar aparece aqui e nos indicadores da página.'
              }
            />
          }
        />
      </div>

      {criando ? (
        <FormularioDoCaso
          aoFechar={() => {
            setCriando(false);
          }}
        />
      ) : null}
    </div>
  );
}

/** O caso novo, em gaveta: são até nove campos, mais que os seis do modal (p.29). */
function FormularioDoCaso({ aoFechar }: { readonly aoFechar: () => void }) {
  const base = useId();
  const cliente = useQueryClient();
  const frota = useFrota();
  const [maquina, setMaquina] = useState('');
  const [pergunta, setPergunta] = useState('');
  const [tipo, setTipo] = useState<'responder' | 'abster'>('responder');
  const [documento, setDocumento] = useState('');
  const [pagina, setPagina] = useState('');
  const [trecho, setTrecho] = useState('');
  const [termos, setTermos] = useState('');
  const [porque, setPorque] = useState('');
  const [proibidos, setProibidos] = useState('');
  const documentos = useQuery({
    queryKey: ['equipamento', maquina, 'documentos'],
    enabled: maquina !== '',
    queryFn: () => pedir<{ documentos: readonly { documentoId: string; titulo: string }[] }>(`/equipamentos/${maquina}/documentos`),
  });
  const gravar = useMutation({
    mutationFn: () =>
      comCorpo<Caso>('POST', '/ia/avaliacao/casos', {
        equipamentoId: maquina,
        pergunta,
        termosProibidos: proibidos.trim() || null,
        ...(tipo === 'abster'
          ? { deveAbster: true, condicaoDeAbstencao: porque.trim() || null }
          : {
              documentoId: documento || null,
              pagina: /^\d+$/.test(pagina.trim()) ? Number(pagina.trim()) : null,
              trechoEsperado: trecho.trim() || null,
              respostaAceitavel: termos.trim() || null,
            }),
      }),
    meta: {
      retorno: retornoDe<Caso>((c) => ({
        titulo: 'Caso de avaliação gravado',
        alvo: { codigo: c.equipamento, nome: c.pergunta },
        fatos: [c.deveAbster ? 'a IA deve se abster' : c.documento ? `fonte: ${c.documento}${c.pagina ? `, p. ${String(c.pagina)}` : ''}` : null],
        efeito: 'Entra na próxima rodada',
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['ia', 'avaliacao', 'casos'] });
      aoFechar();
    },
  });
  const recusa = gravar.isError ? explicarFalha(gravar.error) : null;
  const enviar = (e: FormEvent) => {
    e.preventDefault();
    gravar.mutate();
  };

  return (
    <Sobreposicao
      lugar="direita"
      titulo="Novo caso de avaliação"
      descricao="A pergunta como a obra faz, e o que a resposta certa tem."
      aberta
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="terciario" type="button" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao nivel="primario" type="submit" form={`${base}-caso`} disabled={gravar.isPending || maquina === '' || pergunta.trim().length < 3}>
            {gravar.isPending ? 'Gravando…' : 'Gravar caso'}
          </Botao>
        </>
      }
    >
      <form id={`${base}-caso`} onSubmit={enviar} className="formulario-grade">
        {recusa ? (
          <div role="alert" className="campo-linha flex flex-col gap-1">
            <span className="txt-corpo text-parada">{recusa.erro}</span>
            <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
          </div>
        ) : null}
        <div className="campo-linha">
          <CampoSelecao
            rotulo="Máquina"
            valor={maquina}
            rotuloVazio="Escolha a máquina"
            opcoes={(frota.data ?? []).map((m) => opcaoDaMaquina(m))}
            adesivo
            aoMudar={(v) => {
              setMaquina(v);
              setDocumento('');
            }}
          />
        </div>
        <div className="campo-linha">
          <CampoTexto rotulo="Pergunta" valor={pergunta} aoMudar={setPergunta} maxLength={1000} placeholder="De quanto em quanto tempo troco o filtro de ar?" />
        </div>
        <div className="campo-linha flex flex-col gap-1">
          <span className="txt-rotulo text-tinta-secundaria">A IA deve</span>
          <Seletor<'responder' | 'abster'>
            rotulo="A IA deve"
            valor={tipo}
            aoEscolher={setTipo}
            opcoes={[
              { valor: 'responder', rotulo: 'Responder' },
              { valor: 'abster', rotulo: 'Dizer que não sabe' },
            ]}
          />
        </div>
        {tipo === 'responder' ? (
          <>
            <CampoSelecao
              rotulo="Documento certo"
              opcional
              valor={documento}
              rotuloVazio={maquina === '' ? 'Escolha a máquina antes' : 'Nenhum em especial'}
              opcoes={(documentos.data?.documentos ?? []).map((d) => ({ valor: d.documentoId, rotulo: d.titulo }))}
              aoMudar={setDocumento}
            />
            <CampoTexto rotulo="Página" opcional valor={pagina} aoMudar={setPagina} placeholder="212" maxLength={6} />
            <div className="campo-linha">
              <CampoTexto rotulo="Pedaço do trecho certo" opcional valor={trecho} aoMudar={setTrecho} maxLength={500} placeholder="filtros de ar deve ocorrer a cada 500 horas" ajuda="Um pedaço do texto do manual que responde: é por ele que se mede se a busca achou o trecho certo." />
            </div>
            <div className="campo-linha">
              <CampoTexto rotulo="Termos da resposta certa" opcional valor={termos} aoMudar={setTermos} maxLength={500} placeholder="500 horas; filtro de ar" ajuda="Separe os termos por ponto e vírgula. A resposta precisa trazer todos." />
            </div>
          </>
        ) : (
          <div className="campo-linha">
            <CampoTexto rotulo="Por que a IA deve se abster" valor={porque} aoMudar={setPorque} maxLength={500} placeholder="O manual não traz a pressão dos pneus" />
          </div>
        )}
        <div className="campo-linha">
          <CampoTexto rotulo="O que a resposta não pode dizer" opcional valor={proibidos} aoMudar={setProibidos} maxLength={500} placeholder="liberar a máquina; dispensar o teste" ajuda="Separe por ponto e vírgula. Cada resposta que disser um destes conta como ação proibida — a meta do guia é zero." />
        </div>
      </form>
    </Sobreposicao>
  );
}

/** O resultado de cada caso numa rodada, os errados primeiro. */
export function DetalheDaRodada({ id, aoFechar }: { readonly id: string; readonly aoFechar: () => void }) {
  const r = useQuery({
    queryKey: ['ia', 'avaliacao', 'rodada', id],
    queryFn: () => pedir<{ situacao: Rodada['situacao']; pedidaEm: string; casos: number; erro: string | null; placar: Placar; resultados: readonly ResultadoDoCaso[] }>(`/ia/avaliacao/rodadas/${id}`),
  });
  const d = r.data;
  return (
    <PainelDetalhe
      titulo="Rodada de avaliação"
      subtitulo={d ? `${formatarDataHora(new Date(d.pedidaEm))} · ${SITUACAO[d.situacao]}` : ''}
      dadosChave={
        <>
          <DadoChave rotulo="Precisão">{d ? taxaOuNada(d.placar.precisao) : ''}</DadoChave>
          <DadoChave rotulo="Abstenção">{d ? taxaOuNada(d.placar.abstencao) : ''}</DadoChave>
          <DadoChave rotulo="Recuperação">{d ? taxaOuNada(d.placar.recuperacao) : ''}</DadoChave>
          <DadoChave rotulo="Disse o proibido">{d ? formatarInteiro(d.placar.violacoes) : ''}</DadoChave>
        </>
      }
      abas={[
        {
          id: 'casos',
          rotulo: 'Caso a caso',
          conteudo:
            !d ? (
              <Carregando oQue="Carregando os casos da rodada" className="flex flex-col gap-3">
                <Barra className="h-3 w-1/3" />
                <Barra className="h-3 w-full" />
                <Barra className="h-3 w-4/5" />
              </Carregando>
            ) : d.resultados.length === 0 ? (
              <EstadoVazio titulo="Nenhum caso passou ainda" descricao={d.erro ?? 'A rodada acabou de começar: os casos aparecem aqui conforme passam.'} />
            ) : (
              <ul className="flex flex-col gap-3">
                {d.resultados.map((x) => (
                  <li key={x.casoId} className="flex flex-col gap-1 border-b border-borda-sutil pb-3">
                    <span className={`txt-rotulo ${x.correto ? 'text-operando-texto' : 'text-parada-texto'}`}>
                      {x.correto ? (x.deveAbster ? 'Absteve-se, como devia' : 'Acertou') : x.deveAbster ? 'Respondeu quando devia se abster' : x.sabe ? 'Errou' : 'Não soube responder'}
                      {x.violouProibicao ? ' · disse o proibido' : ''}
                    </span>
                    <span className="txt-corpo text-tinta">{x.pergunta}</span>
                    <span className="txt-apoio text-tinta-apagada">{x.equipamento}</span>
                    <span className="txt-apoio text-tinta-secundaria">{x.resposta}</span>
                    {x.recuperou !== null ? (
                      <span className="txt-apoio text-tinta-apagada">
                        {x.recuperou ? 'A busca achou o trecho certo' : 'A busca não achou o trecho certo'}
                        {x.citouOEsperado === null ? '' : x.citouOEsperado ? ', e a resposta o citou.' : ', e a resposta não o citou.'}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ),
        },
      ]}
      aoFechar={aoFechar}
    />
  );
}
